import React from 'react'
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts'
import { Building2, Crown } from 'lucide-react'
import DashboardCard, { CardSlot } from '@/components/dashboard/DashboardCard'
import { currencySymbol } from '@/lib/calendar-utils'
import { formatMoneySym } from '@/lib/format'
import { listingColor, listingDash } from '@/lib/listing-colors'

/**
 * The seven-night detail strip.
 *
 * `pricedAt` is the COMPUTED LIST PRICE the engine is asking for those nights — it
 * is not ADR and must never be labelled as one. Realized ADR (revenue ÷ rooms sold)
 * lives on the Tonight strip and in the month band; putting a list-price average
 * under the word "rate" beside them is exactly the confusion this rename removes.
 * `revenue` is genuine on-the-books revenue for each night.
 *
 * AT A COMBINED INVENTORY "Priced at" is PER LISTING (`pricedAtListings`): one line per listing,
 * each the average of that listing's own rows, coloured by the listing (lib/listing-colors), and a
 * server sentence in place of a line for a following listing selling at exactly the lead's prices.
 * `pricedAt` is null there by contract: one average over every listing is a price none of them asks.
 * Ungrouped, `pricedAtListings` is null and the card is the single series it always was.
 */

export interface TrendDataPoint {
  date: string
  occupancy: number
  /** Null at a combined inventory, whose prices are per listing (`pricedAtByListing`). */
  pricedAt: number | null
  revenue: number
  /** Combined inventory: one figure per drawn listing, keyed by the series key; null is "no price". */
  pricedAtByListing?: Record<string, number | null>
}

export interface PricedAtSeries {
  key: string
  propertyId: number
  name: string
  isLead: boolean
  role: string
  slot: number
}

export interface PricedAtNote {
  propertyId: number
  name: string
  isLead: boolean
  role: string
  slot: number
  /** The server's sentence: "Same prices as Wharf House", "No prices for these nights". */
  sentence: string
}

export interface PricedAtListings {
  series: PricedAtSeries[]
  notes: PricedAtNote[]
}

interface TrendChartsProps {
  data: TrendDataPoint[]
  currency: string
  pricedAtListings?: PricedAtListings | null
}

function formatDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' })
}

const CustomTooltip = ({ active, payload, label, formatter }: any) => {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg border border-border/60 bg-white px-3 py-2 shadow-lg shadow-black/5">
      <p className="text-[11px] font-medium text-muted-foreground">{label}</p>
      {payload.map((entry: any, i: number) => (
        <p key={i} className="text-sm font-semibold text-foreground tabular-nums">
          {formatter ? formatter(entry.value) : entry.value}
        </p>
      ))}
    </div>
  )
}

function ListingSwatch({ slot }: { slot: number }) {
  return (
    <span
      className="inline-block h-0.5 w-3 shrink-0 rounded-full"
      style={{ backgroundColor: listingColor(slot) }}
      aria-hidden="true"
    />
  )
}

/** One row per drawn listing: its swatch, its name, its price that night (or "No price"). */
const ListingTooltip = ({ active, payload, label, series, sym }: any) => {
  if (!active || !payload?.length) return null
  const point = payload[0]?.payload ?? {}
  return (
    <div className="rounded-lg border border-border/60 bg-white px-3 py-2 shadow-lg shadow-black/5">
      <p className="text-[11px] font-medium text-muted-foreground">{label}</p>
      {(series as PricedAtSeries[]).map((s) => (
        <p key={s.key} className="flex items-center gap-2 text-sm text-foreground">
          <ListingSwatch slot={s.slot} />
          <span className="text-xs text-muted-foreground">{s.name}</span>
          <span className="ml-auto pl-3 font-semibold tabular-nums">
            {point[s.key] != null ? formatMoneySym(point[s.key], sym) : 'No price'}
          </span>
        </p>
      ))}
    </div>
  )
}

/**
 * Priced at, one line per listing. Each line is named in the legend beneath it (identity never rests
 * on colour alone), and a listing folded out of the chart says why, in the server's own words.
 */
function PricedAtByListing({
  chartData,
  listings,
  currency,
  sym,
}: {
  chartData: Record<string, unknown>[]
  listings: PricedAtListings
  currency: string
  sym: string
}) {
  return (
    <DashboardCard
      title="Priced at"
      description={`Computed list price by listing, next 7 nights (${currency})`}
      help={{
        content:
          "The price the engine is asking at each listing for these nights: an average of that listing's own computed list prices across its active rate plans. Each line is one listing. It is not ADR: ADR is revenue actually earned divided by rooms sold.",
        learnMore: '/guides/dashboard#priced-at',
      }}
    >
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={chartData}>
          <CartesianGrid stroke="#e2e8f0" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
          <Tooltip content={<ListingTooltip series={listings.series} sym={sym} />} />
          {listings.series.map((s) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.name}
              stroke={listingColor(s.slot)}
              strokeWidth={2}
              strokeDasharray={listingDash(s.slot)}
              strokeLinecap="round"
              strokeLinejoin="round"
              connectNulls={false}
              dot={{ r: 4, fill: listingColor(s.slot), stroke: '#fff', strokeWidth: 2 }}
              activeDot={{ r: 5, fill: listingColor(s.slot), stroke: '#fff', strokeWidth: 2 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <ul className="mt-3 space-y-1" aria-label="Listings">
        {listings.series.map((s) => {
          const Icon = s.isLead ? Crown : Building2
          return (
            <li key={s.key} className="flex items-center gap-2 text-xs text-foreground">
              <ListingSwatch slot={s.slot} />
              <Icon className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="truncate font-medium">{s.name}</span>
            </li>
          )
        })}
        {listings.notes.map((note) => (
          <li key={note.propertyId} className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="w-3 shrink-0" aria-hidden="true" />
            <Building2 className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span className="truncate">
              <span className="font-medium text-foreground">{note.name}</span>: {note.sentence}
            </span>
          </li>
        ))}
      </ul>
    </DashboardCard>
  )
}

export default function TrendCharts({ data, currency, pricedAtListings }: TrendChartsProps) {
  const sym = currencySymbol(currency)
  // Reshaping only: each listing's figure becomes its own key on the point, the shape Recharts reads.
  const chartData = data.map((d) => ({ ...d, ...(d.pricedAtByListing ?? {}), label: formatDate(d.date) }))

  return (
    <div className="grid gap-4 md:grid-cols-3">
      <CardSlot delay={0}>
        <DashboardCard
          title="Occupancy"
          description="On the books, next 7 nights"
          help={{
            content: 'Rooms confirmed for each of the next seven nights as a share of your total rooms.',
          }}
        >
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="occGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0d9488" stopOpacity={0.15} />
                  <stop offset="100%" stopColor="#0d9488" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} domain={[0, 100]} unit="%" axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip formatter={(v: number) => `${v}%`} />} />
              <Area
                type="monotone"
                dataKey="occupancy"
                stroke="#0d9488"
                strokeWidth={2.5}
                fill="url(#occGrad)"
                dot={{ r: 3, fill: '#0d9488', strokeWidth: 0 }}
                activeDot={{ r: 5, fill: '#0d9488', strokeWidth: 2, stroke: '#fff' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </DashboardCard>
      </CardSlot>

      <CardSlot delay={50}>
        {pricedAtListings ? (
          <PricedAtByListing chartData={chartData} listings={pricedAtListings} currency={currency} sym={sym} />
        ) : (
        <DashboardCard
          title="Priced at"
          description={`Computed list price, next 7 nights (${currency})`}
          help={{
            content:
              'The price the engine is currently asking for these nights — an average of computed list prices across your rate plans. It is not ADR: ADR is revenue actually earned divided by rooms sold, and shows on the Tonight strip and the month band.',
            learnMore: '/guides/how-it-works',
          }}
        >
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="pricedAtGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity={0.15} />
                  <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip formatter={(v: number) => formatMoneySym(v, sym)} />} />
              <Area
                type="monotone"
                dataKey="pricedAt"
                stroke="#6366f1"
                strokeWidth={2.5}
                fill="url(#pricedAtGrad)"
                dot={{ r: 3, fill: '#6366f1', strokeWidth: 0 }}
                activeDot={{ r: 5, fill: '#6366f1', strokeWidth: 2, stroke: '#fff' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </DashboardCard>
        )}
      </CardSlot>

      <CardSlot delay={100}>
        <DashboardCard
          title="Revenue"
          description="On the books, next 7 nights"
          help={{
            content: 'Confirmed accommodation revenue for each of the next seven nights — the same figures the month band totals.',
          }}
        >
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={chartData}>
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0d9488" stopOpacity={0.85} />
                  <stop offset="100%" stopColor="#0d9488" stopOpacity={0.5} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip formatter={(v: number) => formatMoneySym(v, sym)} />} />
              <Bar dataKey="revenue" fill="url(#revGrad)" radius={[6, 6, 0, 0]} maxBarSize={40} />
            </BarChart>
          </ResponsiveContainer>
        </DashboardCard>
      </CardSlot>
    </div>
  )
}
